import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Globe, Check, RefreshCw, Trash2, Copy, AlertTriangle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(null);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_URL}/domains`);
      setDomains(response.data);
    } catch (error) {
      toast.error('Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    setAdding(true);
    try {
      const response = await axios.post(`${API_URL}/domains`, { domain: newDomain.trim() });
      toast.success('Domain added! Add the DNS records below to verify it.');
      setDialogOpen(false);
      setNewDomain('');
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (domainId) => {
    setVerifying(domainId);
    try {
      const response = await axios.post(`${API_URL}/domains/${domainId}/verify`);
      if (response.data.verified) {
        toast.success('Domain verified successfully!');
      } else {
        toast.info(response.data.message || 'DNS records not yet valid. Please wait for propagation.');
      }
      fetchDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification check failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleDelete = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;

    try {
      await axios.delete(`${API_URL}/domains/${domainId}`);
      toast.success('Domain deleted');
      fetchDomains();
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="domains-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sending Domains</h1>
          <p className="text-muted-foreground mt-1">Authenticate domains via SendGrid for email sending</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-domain-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Sending Domain</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="domain">Domain Name</Label>
                <Input
                  id="domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  data-testid="domain-input"
                />
                <p className="text-xs text-muted-foreground">
                  This will create a domain authentication in your SendGrid account and provide DNS records to add.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddDomain} disabled={adding} data-testid="save-domain-btn">
                {adding ? 'Adding...' : 'Add Domain'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Domains List */}
      {domains.length > 0 ? (
        <div className="space-y-4">
          {domains.map((domain) => (
            <Card key={domain.id} data-testid={`domain-${domain.id}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      domain.verified ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                    }`}>
                      <Globe className={`w-6 h-6 ${
                        domain.verified ? 'text-emerald-500' : 'text-amber-500'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{domain.domain}</h3>
                        {domain.verified ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                            <Check className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400">
                            Pending DNS
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Daily limit: {domain.daily_sent_count || 0} / {domain.daily_cap || 2000}
                        <button
                          className="ml-2 text-xs text-primary hover:underline"
                          onClick={async () => {
                            const newCap = window.prompt('Set daily send limit:', domain.daily_cap || 2000);
                            if (newCap && !isNaN(newCap)) {
                              try {
                                await axios.put(`${API_URL}/domains/${domain.id}/daily-cap`, { daily_cap: parseInt(newCap) });
                                toast.success(`Daily cap updated to ${newCap}`);
                                fetchDomains();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to update');
                              }
                            }
                          }}
                        >
                          Edit
                        </button>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!domain.verified && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleVerify(domain.id)}
                        disabled={verifying === domain.id}
                        data-testid={`verify-domain-${domain.id}`}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${verifying === domain.id ? 'animate-spin' : ''}`} />
                        {verifying === domain.id ? 'Checking...' : 'Verify DNS'}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(domain.id)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-domain-${domain.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* DNS Records from SendGrid */}
                {domain.dns_records?.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-muted/50">
                    <h4 className="text-sm font-medium mb-2">DNS Records to Add</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Add these records to your DNS provider (Namecheap, Cloudflare, GoDaddy, etc.):
                    </p>
                    <div className="space-y-3">
                      {domain.dns_records.map((record, idx) => (
                        <div key={idx} className="p-3 rounded bg-background border">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{record.type}</Badge>
                              {record.valid ? (
                                <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">Valid</Badge>
                              ) : (
                                <Badge className="bg-amber-500/10 text-amber-600 text-xs">Pending</Badge>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1 mt-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-12 flex-shrink-0">Host:</span>
                              <code className="text-xs font-mono flex-1 truncate">{record.host}</code>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(record.host)}>
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground w-12 flex-shrink-0">Value:</span>
                              <code className="text-xs font-mono flex-1 truncate">{record.data}</code>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(record.data)}>
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No DNS records but not verified */}
                {(!domain.dns_records || domain.dns_records.length === 0) && !domain.verified && (
                  <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">No DNS records available</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          This might mean the domain already exists in your SendGrid account. 
                          Try deleting this domain and re-adding it, or authenticate it directly in SendGrid dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">No domains yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Add a domain to start sending emails via SendGrid</p>
            <Button onClick={() => setDialogOpen(true)} data-testid="add-domain-empty-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Domain
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex items-start gap-3 pt-6">
          <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium">Domain Authentication with SendGrid</p>
            <p className="text-sm text-muted-foreground mt-1">
              When you add a domain, SendGrid creates authentication records (CNAME) that you need to add to your DNS provider. 
              This verifies you own the domain and enables DKIM signing for better deliverability. 
              DNS changes can take up to 48 hours to propagate.
            </p>
            <a
              href="https://app.sendgrid.com/settings/sender_auth/domains"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
            >
              Manage domains in SendGrid Dashboard
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Domains;
